/**
 * Web Component Wrapper
 *
 * Converts the Widget React component into a custom element <bolder-widget>
 * Features:
 * - Shadow DOM for style isolation
 * - Attribute observation for reactive updates
 * - Secure parameter parsing
 */

import { createRoot, Root } from 'react-dom/client';

import { FloatingWrapper, FloatingWrapperProps } from './components/FloatingWrapper';
import { debugLog } from './debug';
import type { WidgetProps } from './Widget';
import { parseWidgetAttributes, WIDGET_OBSERVED_ATTRIBUTES } from './widgetAttributes';

/**
 * Origin of the deployment that served this script (captured while it runs).
 * A saved widget's design is read from there, so the install code needs no
 * URLs beyond the script tag.
 */
const SCRIPT_ORIGIN = (() => {
  try {
    const script =
      typeof document !== 'undefined' ? (document.currentScript as HTMLScriptElement | null) : null;
    return script?.src ? new URL(script.src).origin : '';
  } catch {
    return '';
  }
})();

type SavedWidgetState =
  | { status: 'idle' }
  | { status: 'loading'; widgetId: string }
  | { status: 'ready'; widgetId: string; attributes: Record<string, string> }
  | { status: 'unavailable'; widgetId: string; reason: string };

// Styles to inject into Shadow DOM
const shadowStyles = `
  :host {
    display: flex;
    flex-direction: column;
    contain: layout style;
    min-height: 0;
  }

  :host([hidden]) {
    display: none;
  }

  * {
    box-sizing: border-box;
  }

  .bolder-widget-container {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  @keyframes bolder-spin {
    to { transform: rotate(360deg); }
  }
`;

class BolderWidgetElement extends HTMLElement {
  private root: Root | null = null;
  private saved: SavedWidgetState = { status: 'idle' };
  private widgetContainer: HTMLDivElement | null = null;
  private usesShadowDOM: boolean = true;
  private _onCollapse?: () => void;

  // Public property setter for onCollapse callback
  public set onCollapse(callback: (() => void) | undefined) {
    this._onCollapse = callback;
    // Re-render when callback is set
    if (this.root) {
      this.render();
    }
  }

  public get onCollapse(): (() => void) | undefined {
    return this._onCollapse;
  }

  // Observed attributes for reactive updates
  static get observedAttributes(): string[] {
    return WIDGET_OBSERVED_ATTRIBUTES;
  }

  constructor() {
    super();
    debugLog('[BolderWidget WebComponent] Constructor called');
    // Check embed-mode attribute to determine if we should use shadow-sm DOM
    const embedMode = this.getAttribute('embed-mode') || 'embed';
    this.usesShadowDOM = embedMode !== 'normal';

    if (this.usesShadowDOM) {
      this.attachShadow({ mode: 'open' });
      debugLog('[BolderWidget WebComponent] Shadow DOM attached:', !!this.shadowRoot);
    } else {
      debugLog('[BolderWidget WebComponent] Normal mode - no Shadow DOM');
    }
  }

  connectedCallback(): void {
    debugLog('[BolderWidget WebComponent] connectedCallback - element added to DOM');

    // Handle properties that may have been set before the element was upgraded
    // This is necessary because if someone sets widget.onCollapse = fn before
    // the custom element is defined, it sets a plain property that shadows our getter
    this._upgradeProperty('onCollapse');

    this.setupDOM();
    this.render();
    try {
      this.colorSchemeQuery = window.matchMedia('(prefers-color-scheme: dark)');
      this.colorSchemeQuery.addEventListener('change', this.handleColorSchemeChange);
    } catch {
      // matchMedia unavailable — theme="auto" resolves once
    }
    this.dispatchEvent(new CustomEvent('bolder-widget:connected'));
    debugLog('[BolderWidget WebComponent] connectedCallback complete');
  }

  /**
   * Handles properties that may have been set on the element before it was upgraded.
   * This ensures that property setters are properly invoked for pre-set values.
   */
  private _upgradeProperty(prop: string): void {
    if (Object.prototype.hasOwnProperty.call(this, prop)) {
      const value = (this as any)[prop];
      delete (this as any)[prop];
      (this as any)[prop] = value;
      debugLog(`[BolderWidget WebComponent] Upgraded pre-set property: ${prop}`, !!value);
    }
  }

  disconnectedCallback(): void {
    this.cleanup();
    this.dispatchEvent(new CustomEvent('bolder-widget:disconnected'));
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue !== newValue && this.root) {
      if (name === 'widget-id') this.saved = { status: 'idle' };
      this.render();
    }
  }

  /** Reads a saved widget's attribute map from the serving deployment (GET /api/widget/<id>). */
  private async loadSavedWidget(widgetId: string): Promise<void> {
    this.saved = { status: 'loading', widgetId };
    const base = (this.getAttribute('app-base-url') || SCRIPT_ORIGIN || window.location.origin).replace(
      /\/$/,
      '',
    );
    let next: SavedWidgetState;
    try {
      const response = await fetch(`${base}/api/widget/${encodeURIComponent(widgetId)}`, {
        credentials: 'omit',
      });
      const body = await response.json().catch(() => null);
      next =
        response.ok && body?.attributes && typeof body.attributes === 'object'
          ? { status: 'ready', widgetId, attributes: body.attributes as Record<string, string> }
          : { status: 'unavailable', widgetId, reason: body?.error || `HTTP ${response.status}` };
    } catch (error) {
      next = { status: 'unavailable', widgetId, reason: String(error) };
    }
    // Ignore a response for a widget id that has since changed.
    if (this.getAttribute('widget-id') !== widgetId) return;
    this.saved = next;
    if (next.status === 'unavailable') {
      console.warn(
        next.reason === 'DOMAIN_NOT_ALLOWED'
          ? `[BolderWidget] Widget ${widgetId} is not enabled for ${window.location.host}. Add this site in Bolder → Distribute → Install.`
          : `[BolderWidget] Widget ${widgetId} could not be loaded (${next.reason}).`,
      );
    }
    this.render();
  }

  /** Element attributes win over the saved widget's, so a page can override one setting. */
  private readAttribute = (name: string): string | null => {
    if (this.hasAttribute(name)) return this.getAttribute(name);
    return this.saved.status === 'ready' ? this.saved.attributes[name] ?? null : null;
  };

  /** Inline / full-page widgets size their own host when the site hasn't. */
  private sizeHost(): void {
    const layout = this.readAttribute('layout');
    if (layout !== 'inline' && layout !== 'fullpage') return;
    if (!this.style.display) this.style.display = 'block';
    if (!this.style.height) {
      const height = parseInt(this.readAttribute('embed-height') || '', 10);
      this.style.height = layout === 'fullpage' ? '100vh' : `${Number.isFinite(height) ? height : 640}px`;
    }
  }

  private setupDOM(): void {
    debugLog('[BolderWidget WebComponent] setupDOM called, usesShadowDOM:', this.usesShadowDOM);

    if (this.usesShadowDOM && this.shadowRoot) {
      // Shadow DOM mode: Inject styles and create container in shadow-sm root
      const styleSheet = document.createElement('style');
      styleSheet.textContent = shadowStyles;
      this.shadowRoot.appendChild(styleSheet);

      this.widgetContainer = document.createElement('div');
      this.widgetContainer.className = 'bolder-widget-container';
      // Flex container styles for proper scroll handling
      this.widgetContainer.style.display = 'flex';
      this.widgetContainer.style.flexDirection = 'column';
      this.widgetContainer.style.width = '100%';
      this.widgetContainer.style.height = '100%';
      this.widgetContainer.style.flex = '1';
      this.widgetContainer.style.minHeight = '0';
      this.widgetContainer.style.overflow = 'hidden';
      this.shadowRoot.appendChild(this.widgetContainer);
      debugLog('[BolderWidget WebComponent] Shadow DOM setup complete');
    } else {
      // Normal mode: Create container directly in the element (light DOM)
      this.widgetContainer = document.createElement('div');
      this.widgetContainer.className = 'bolder-widget-container';
      // Flex container styles for proper scroll handling
      this.widgetContainer.style.display = 'flex';
      this.widgetContainer.style.flexDirection = 'column';
      this.widgetContainer.style.width = '100%';
      this.widgetContainer.style.height = '100%';
      this.widgetContainer.style.flex = '1';
      this.widgetContainer.style.minHeight = '0';
      this.widgetContainer.style.overflow = 'hidden';
      this.appendChild(this.widgetContainer);
      debugLog('[BolderWidget WebComponent] Normal DOM setup complete');
    }
  }

  private getProps(): FloatingWrapperProps {
    return {
      ...parseWidgetAttributes(this.readAttribute),
      onReady: () => {
        this.dispatchEvent(new CustomEvent('bolder-widget:ready'));
      },
      onEvent: (eventName, data) => {
        this.dispatchEvent(
          new CustomEvent(`bolder-widget:${eventName}`, {
            detail: data,
            bubbles: true,
            composed: true, // Cross shadow-sm DOM boundary
          }),
        );
      },
      onCollapse: this.onCollapse,
    };
  }

  /** theme="auto" follows the visitor's OS light/dark setting, live. */
  private colorSchemeQuery: MediaQueryList | null = null;
  private handleColorSchemeChange = (): void => {
    if (this.root && this.readAttribute('theme') === 'auto') this.render();
  };

  private render(): void {
    debugLog('[BolderWidget WebComponent] render called, widgetContainer:', !!this.widgetContainer);
    if (!this.widgetContainer) {
      console.error('[BolderWidget WebComponent] render aborted - no widgetContainer');
      return;
    }

    if (!this.root) {
      debugLog('[BolderWidget WebComponent] Creating React root');
      this.root = createRoot(this.widgetContainer);
    }

    // A saved widget renders nothing until its design arrives (or if it can't).
    const widgetId = this.getAttribute('widget-id');
    if (widgetId) {
      if (this.saved.status === 'idle' || this.saved.widgetId !== widgetId) {
        void this.loadSavedWidget(widgetId);
      }
      if (this.saved.status !== 'ready') {
        this.root.render(null);
        return;
      }
    }
    this.sizeHost();

    const props = this.getProps();
    debugLog('[BolderWidget WebComponent] render props:', props);

    debugLog('[BolderWidget WebComponent] Rendering FloatingWrapper component');
    this.root.render(<FloatingWrapper {...props} />);
    debugLog('[BolderWidget WebComponent] FloatingWrapper rendered');
  }

  private cleanup(): void {
    this.colorSchemeQuery?.removeEventListener('change', this.handleColorSchemeChange);
    this.colorSchemeQuery = null;
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }

  // Public API methods
  public updateConfig(config: Partial<WidgetProps>): void {
    Object.entries(config).forEach(([key, value]) => {
      const attrName = key.replace(/([A-Z])/g, '-$1').toLowerCase();
      if (value !== undefined && value !== null) {
        this.setAttribute(attrName, String(value));
      } else {
        this.removeAttribute(attrName);
      }
    });
  }

  public destroy(): void {
    this.cleanup();
    this.remove();
  }
}

// Register the custom element
function registerBolderWidget(): void {
  debugLog('[BolderWidget WebComponent] registerBolderWidget called');
  if (typeof window === 'undefined') {
    debugLog('[BolderWidget WebComponent] window undefined, skipping registration');
    return;
  }

  if (!customElements.get('bolder-widget')) {
    debugLog('[BolderWidget WebComponent] Registering <bolder-widget> custom element');
    customElements.define('bolder-widget', BolderWidgetElement);
    debugLog('[BolderWidget WebComponent] Custom element registered successfully');
  } else {
    debugLog('[BolderWidget WebComponent] Custom element already registered');
  }
}

// Auto-register when script loads
debugLog('[BolderWidget WebComponent] Script loaded, calling registerBolderWidget');
registerBolderWidget();

// Export for manual registration if needed
export { BolderWidgetElement, registerBolderWidget };
